/* eslint-disable no-nested-ternary */
import { generatePath } from 'react-router-dom';

import { ROUTES } from '#client/manifest';
import { hasSkippedDomain } from '#client/utils/domains';
import { buildCartUrl, generateLogoBuilderURL } from '#client/utils/urls';

import { processMenu } from '../utils/menu';

/**
 * Get the tooltip for the email menu item.
 *
 * @param {object} entitlements
 *
 * @returns {string|null}
 */
function getEmailTooltip(entitlements) {
  const hasDomain = entitlements.has('domain_name_reg');
  const hasEmail = entitlements.has('basic_email');
  const didSkipDomain = hasSkippedDomain(entitlements.get('domain_name_reg'));
  const isEmailActive = entitlements.get('basic_email')?.isActive;

  // Return tooltip if domain has not been purchased.
  if (!hasDomain) {
    return 'Email requires the purchase of a Domain';
  }

  // Return tooltip if domain was skipped.
  if (didSkipDomain) {
    return 'You’ve purchased a domain. To activate it, chat with us or email us at support@zenbusiness.com';
  }

  // Return tooltip if email has been purchased but not active.
  if (hasEmail && !isEmailActive) {
    return 'Your email configuration is being setup. ZenBusiness customer support will be in contact soon.';
  }

  return null;
}

/**
 * Get the Web Tools menu items.
 *
 * @param {{
 *  businessEntityUuid: string
 *  entitlements: Set<string>
 *  isLBTRTestEnabled: boolean
 *  isLBTRTestActive: boolean
 * }} params
 * @returns {Array}
 */
export function getWebToolsMenuItems({
  businessEntityUuid,
  entitlements,
  isLBTRTestActive,
  isLBTRTestEnabled,
  isMobile
}) {
  const hasEmail = entitlements.has('basic_email');
  const hasDomain = entitlements.has('domain_name_reg');
  const hasLogoBuilder = entitlements.has('logo_builder');

  const comingSoonPage = '/d/company/:businessEntityUuid/marketing-tools/logo-builder/coming-soon';

  return [
    {
      order: 10,
      title: 'Overview',
      path: generatePath(ROUTES.marketingTools, { businessEntityUuid })
    },
    {
      order: 20,
      title: 'Website',
      path: generatePath(ROUTES.websiteList, { businessEntityUuid })
    },
    {
      order: 30,
      title: 'Domain Names',
      path: hasDomain ? generatePath(ROUTES.domainList, { businessEntityUuid }) : null,
      url: !hasDomain ? buildCartUrl('domain_name_reg', { businessEntityUuid }) : null
    },
    {
      order: 40,
      title: 'Business Email',
      target: hasEmail ? '_blank' : null,
      url: hasEmail ? 'https://mail.zenbusiness.com/' : buildCartUrl('basic_email', { businessEntityUuid }),
      tooltip: getEmailTooltip(entitlements)
    },
    {
      show: isLBTRTestEnabled,
      order: 50,
      title: 'Logo Builder',
      target: '_blank',
      url: ((isLBTRTestActive || hasLogoBuilder) && !isMobile)
        ? generateLogoBuilderURL(businessEntityUuid) : null,
      path: ((!isLBTRTestActive && !hasLogoBuilder) || isMobile)
        ? generatePath(comingSoonPage, { businessEntityUuid }) : null,
      isBeta: true,
      eventProps: {
        experimentName: 'Logo Builder 1.0',
        experimentVariation: 'Beta Launch'
      }
    }
  ];
}

/**
 * Get the web tools menu.
 *
 * @param {{
 *  businessEntityUuid: string
 *  entitlements: Set<string>
 *  order?: number
 * }} params
 * @returns {object}
 */
export function getWebToolsMenu({
  businessEntityUuid,
  entitlements,
  ldFlags,
  order,
  isMobile
}) {
  const { isLBTRTestActive, isLBTRTestEnabled } = ldFlags;
  return processMenu({
    title: 'Web Tools',
    icon: 'webpage-fill.svg',
    links: getWebToolsMenuItems({
      businessEntityUuid,
      entitlements,
      isLBTRTestActive,
      isLBTRTestEnabled,
      isMobile
    }),
    order
  });
}
